import * as React from 'react';
import { isMobileStyleMixin, Text, useIsMobile } from '@amzn/storm-ui';
import { IA2AccountDropdownButton } from './styles';
import styled, { css } from 'styled-components';
import { gray200, gray600, white } from 'src/colors';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import COUNTRY_LOCALIZATIONS from 'src/constants/country-localization';
import { MessageBundle } from '@amzn/i18n.panther-resource-resolver';
import { ACCOUNT_TYPE_PILL_LABEL_MAP } from './AccountTypeLocalizationKeyMapping';
import {
  AccountGroup,
  AdvertisingAccountType,
} from '@amzn/aac-chrome-internal-api';
import { getGlobalSponsoredAdsAccountFromAccountGroup } from './accountHelper';

const ACCOUNT_NAME_MAX_DISPLAY_LENGTH = 50;
const ACCOUNT_NAME_FOLLOW_UP_STRING = '...';

export type AccountDropdownTriggerProps = {
  handleToggle: () => void;
  isOpen: boolean;
};

type AccountDropdownTriggerCommonProps = {
  accountName: string;
  marketplaceLabel: string;
  allAcountsLabel: string;
  isTriggerEnabled: boolean;
  handleToggle: () => void;
  isOpen: boolean;
};

const AccountName = styled(Text)`
  color: ${white};
  line-height: 0.9rem;
  ${isMobileStyleMixin(css`
    white-space: initial;
    overflow-wrap: anywhere;
  `)}
`;

const MarketplaceInfo = styled(Text)`
  color: ${gray200};
  line-height: 0.9rem;
`;

const ButtonInnerContainer = styled.div`
  display: flex;
  align-items: center;
`;

const TextContainer = styled.div`
  text-align: left;
  padding: 0 7px;
  max-width: 100%;
`;

const SvgIcon = styled.svg`
  width: 20px;
  height: 20px;
  fill: ${gray600};
  flex-shrink: 0;
`;

export const getAccountTypeLabel = ({
  accounts,
  currentEntityId,
  currentAccountType,
  currentMarketplaceId,
  bundle,
}: {
  accounts: AccountGroup[];
  currentEntityId: string;
  currentAccountType: AdvertisingAccountType;
  currentMarketplaceId: string;
  bundle: MessageBundle;
}): string => {
  const parentGlobalSponsoredAdsAccount =
    getGlobalSponsoredAdsAccountFromAccountGroup(
      currentEntityId,
      currentAccountType,
      accounts,
    );
  if (
    parentGlobalSponsoredAdsAccount?.nestedAccounts &&
    parentGlobalSponsoredAdsAccount.nestedAccounts.length > 1
  ) {
    return bundle.getMessage('account-switcher-pill-global-sa-label');
  }

  const accountTypeLabel = ACCOUNT_TYPE_PILL_LABEL_MAP.get(currentAccountType);

  if (
    accountTypeLabel &&
    currentAccountType === AdvertisingAccountType.SPONSORED_ADS
  ) {
    const countryCode = COUNTRY_LOCALIZATIONS.get(currentMarketplaceId);
    return bundle.formatMessage(accountTypeLabel, {
      marketplaceName: countryCode
        ? bundle.getMessage(`account-switcher-marketplace-${countryCode}`)
        : '',
    });
  }

  if (accountTypeLabel) {
    return bundle.getMessage(accountTypeLabel);
  }

  return '';
};

//TODO: update to read CENTRIC_ACCOUNT_CARD_TYPE when Sauron currentAccountType align with CENTRIC
export const getCentricAccountTypeLabel = ({
  currentAccountType,
  currentMarketplaceId,
  bundle,
}: {
  currentAccountType: AdvertisingAccountType;
  currentMarketplaceId: string;
  bundle: MessageBundle;
}): string => {
  const accountTypeLabel = ACCOUNT_TYPE_PILL_LABEL_MAP.get(currentAccountType);

  if (
    accountTypeLabel &&
    currentAccountType === AdvertisingAccountType.SPONSORED_ADS
  ) {
    const countryCode = COUNTRY_LOCALIZATIONS.get(currentMarketplaceId);
    return bundle.formatMessage(accountTypeLabel, {
      marketplaceName: countryCode
        ? bundle.getMessage(`account-switcher-marketplace-${countryCode}`)
        : '',
    });
  }

  if (accountTypeLabel) {
    return bundle.getMessage(accountTypeLabel);
  }

  return '';
};

export const AccountDropdownTriggerCommon: React.FC<
  AccountDropdownTriggerCommonProps
> = ({
  accountName,
  marketplaceLabel,
  allAcountsLabel,
  isTriggerEnabled,
  isOpen,
  handleToggle,
}) => {
  const chromeConfig = useChromeConfigContext();
  const isMobile = useIsMobile();
  const isAllAccountsPageScope =
    chromeConfig.pageHeader?.pageScope === 'ALL_ACCOUNTS';
  return (
    <IA2AccountDropdownButton
      variant={
        isAllAccountsPageScope || !isTriggerEnabled ? 'inactive' : 'active'
      }
      {...(isTriggerEnabled ? { onClick: handleToggle } : {})}
      active={isOpen}
      data-ccx-e2e-id="aac-account-dropdown"
      aria-haspopup={true}
      aria-expanded={isOpen}
    >
      <ButtonInnerContainer>
        <SvgIcon
          xmlns="http://www.w3.org/2000/svg"
          data-testid="icon-globe"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M20 10c0-5.523-4.477-10-10-10S0 4.477 0 10s4.477 10 10 10l.314-.005C15.692 19.83 20 15.418 20 10Zm-9.974 8.25-.106-.007c-.868-.115-1.879-1.71-2.457-4.11l5.112.008c-.58 2.405-1.596 4.001-2.466 4.104l-.083.005Zm-3.074-.581-.105-.193c-.497-.924-.901-2.062-1.18-3.345l-2.813-.005a8.28 8.28 0 0 0 4.098 3.543Zm10.18-3.521a8.284 8.284 0 0 1-4.034 3.5c.54-.95.976-2.146 1.272-3.504l2.763.004ZM14.799 10c0 .825-.047 1.627-.137 2.395l3.235.005a8.25 8.25 0 0 0 .354-2.4 8.25 8.25 0 0 0-.35-2.384l-3.239-.005c.09.766.137 1.566.137 2.389Zm-7.655 2.382A18.65 18.65 0 0 1 6.992 10c0-.84.055-1.645.153-2.4l5.751.009c.098.752.152 1.554.152 2.391s-.054 1.639-.152 2.391l-5.753-.009ZM1.75 10c0 .825.121 1.623.347 2.375l3.281.005A20.578 20.578 0 0 1 5.242 10c0-.828.048-1.633.139-2.403l-3.274-.005A8.25 8.25 0 0 0 1.75 10Zm11.444-7.476-.097-.173a8.28 8.28 0 0 1 4.043 3.513L14.37 5.86c-.278-1.279-.682-2.414-1.177-3.336Zm-3.177-.774c-.885 0-1.948 1.618-2.55 4.1l5.108.008c-.578-2.394-1.587-3.985-2.454-4.101l-.104-.007ZM6.9 2.353l.054-.021-.16.293c-.47.9-.853 1.995-1.12 3.222l-2.8-.004a8.28 8.28 0 0 1 4.026-3.49Z"
          />
        </SvgIcon>
        {isAllAccountsPageScope && (
          <TextContainer>
            <AccountName>{allAcountsLabel}</AccountName>
          </TextContainer>
        )}
        {!isAllAccountsPageScope && (
          <TextContainer>
            {!isMobile && (
              <AccountName fontSize="small">
                {accountName.length > ACCOUNT_NAME_MAX_DISPLAY_LENGTH
                  ? accountName
                      .substring(0, ACCOUNT_NAME_MAX_DISPLAY_LENGTH)
                      .concat(ACCOUNT_NAME_FOLLOW_UP_STRING)
                  : accountName}
              </AccountName>
            )}
            {isMobile && (
              <AccountName fontSize="small">{accountName}</AccountName>
            )}
            <MarketplaceInfo fontSize="small" textColor="tertiary">
              {marketplaceLabel}
            </MarketplaceInfo>
          </TextContainer>
        )}
      </ButtonInnerContainer>
    </IA2AccountDropdownButton>
  );
};
